import { useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useBlocker } from "react-router";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { FormStepper } from "sj-ui-toolkit/src/components";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes, apiAssignmentPath } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { getRailsClient } from "sj-ui-toolkit/src/client";

import { AppContext } from "@/context/AppContext";
import {
  loadAssignmentPoliciesAsync,
  loadAssignmentPolicyByIdAsync,
  createAssignmentAsync,
  updateAssignmentPolicyAsync,
  loadAssignedUsersAsync,
  loadAssignmentPolicyRulesAsync,
  createAssignmentPolicyRuleAsync,
  deleteAssignmentPolicyRuleAsync,
  deleteAssignmentPolicyAsync,
  deleteAssignmentUsersAsync,
  loadPathsAsync,
  loadLevelsAsync,
  transformApiDataToFormValues,
  setProcessingAssignment,
  isAssignmentProcessing,
} from "@/api/assignments";
import type {
  FormValues,
  FormSettings,
  Path,
  Level,
  AssignmentPolicyData,
  StoredUser,
  CreateAssignmentPayload,
  NewHireRulePayload,
} from "@/api/assignments";
import { normalizeNotificationsFrequency } from "@/helpers/assignmentHelpers";

import { useAvailableUsers } from "@/hooks/useAvailableUsers";
import { useUnsavedUsersWarning } from "@/hooks/useUnsavedUsersWarning";

import PathSelection from "@/components/assignments/PathSelection";
import AssignmentAudience from "@/components/assignments/AssignmentAudience";
import AssignmentSettings from "@/components/assignments/AssignmentSettings";
import AssignmentReview from "@/components/assignments/AssignmentReview";
import UserSelectionConfirmDialog from "@/components/assignments/UserSelectionConfirmDialog";
import { SubmitProgressDialog } from "@/components/assignments/SubmitProgressDialog";
import DeleteAssignmentButton from "@/components/assignments/DeleteAssignmentButton";

// ─── Validation error shape ───────────────────────────────────────────────────

interface ValidationErrors {
  path: { hasError: boolean; message: string };
  audience: { hasError: boolean; message: string };
  settings: {
    name: { hasError: boolean; message: string };
    description: { hasError: boolean; message: string };
    date: { hasError: boolean; message: string };
  };
}

// ─── Page ────────────────────────────────────────────────────────────────────

const NewAssignment = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const { showToast } = useToast();

  const { state } = useContext(AppContext);
  const { viewOnly } = state;
  const tenantName = getCurrentSelectedTenant() ?? "";

  const {
    availableUsers,
    allUsers,
    isLoading: isLoadingAvailableUsers,
  } = useAvailableUsers(tenantName);

  const [currentStep, setCurrentStep] = useState(0);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const [availablePaths, setAvailablePaths] = useState<Path[]>([]);
  const [levelOptions, setLevelOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [isLoadingPaths, setIsLoadingPaths] = useState(true);
  const [existingAssignments, setExistingAssignments] = useState<AssignmentPolicyData[]>([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);
  const [isLoadingAssignmentDetails, setIsLoadingAssignmentDetails] = useState(isEditMode);

  const [formValues, setFormValues] = useState<FormValues>({
    selectedPath: null,
    target: { availableUsers: [], users: [] },
    settings: {},
  });

  const [originalUsers, setOriginalUsers] = useState<StoredUser[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [usersWereModified, setUsersWereModified] = useState(false);

  const [showAudienceProcessingMessage, setShowAudienceProcessingMessage] = useState(() =>
    isAssignmentProcessing(id)
  );

  useEffect(() => {
    if (!id) return;
    const interval = setInterval(() => {
      setShowAudienceProcessingMessage(isAssignmentProcessing(id));
    }, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const {
    showUserSelectionDialog,
    selectedUsersToAdd,
    setSelectedUsersToAdd,
    selectedUsersToRemove,
    setSelectedUsersToRemove,
    addUsersAndContinueRef,
    handleSelectedUsersChange,
    handleContinue,
    handleCancel: handleCancelDialog,
    handleAddUsersAndContinue,
    checkUnsavedUsers,
  } = useUnsavedUsersWarning(setCurrentStep);

  const [selectedLevel, setSelectedLevel] = useState<string[]>([]);
  const [newHire, setNewHire] = useState(false);
  const [newHireRuleId, setNewHireRuleId] = useState<string | null>(null);

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    path: { hasError: true, message: "Please select a path" },
    audience: { hasError: false, message: "" },
    settings: {
      name: { hasError: true, message: "Assignment name is required" },
      description: { hasError: true, message: "Assignment description is required" },
      date: { hasError: true, message: "Please select an end date" },
    },
  });

  // ─── Breadcrumb ───────────────────────────────────────────────────────────

  useBreadcrumb(
    isEditMode
      ? {
          label: (formValues.settings as FormSettings & { name?: string }).name ?? "Assignment",
          route: AdminAppRoutes.routes.assignments.routes.id.route.replace(":id", id ?? ""),
          display: "page",
        }
      : {
          label: "New Assignment",
          route: AdminAppRoutes.routes.assignments.routes.new.route,
          display: "page",
        }
  );

  // ─── Computed values ──────────────────────────────────────────────────────

  const unsavedAudienceChangesCount = useMemo(() => {
    if (!isEditMode) return 0;
    const currentUsers = formValues.target.users ?? [];
    const added = currentUsers.filter(
      (u: StoredUser) => !originalUsers.some(o => o.directoryUuid === u.directoryUuid)
    );
    const removed = originalUsers.filter(
      (o: StoredUser) => !currentUsers.some((u: StoredUser) => u.directoryUuid === o.directoryUuid)
    );
    return added.length + removed.length;
  }, [isEditMode, formValues.target.users, originalUsers]);

  const isNameUnique = useMemo(() => {
    const name = (formValues.settings as FormSettings & { name?: string }).name?.trim();
    if (!name) return true;
    return !existingAssignments.some(a => a.name?.trim() === name && String(a.id) !== String(id));
  }, [formValues.settings, existingAssignments, id]);

  const isFormValid = useMemo(
    () =>
      !validationErrors.path.hasError &&
      !validationErrors.settings.name.hasError &&
      !validationErrors.settings.description.hasError &&
      !validationErrors.settings.date.hasError &&
      isNameUnique,
    [validationErrors, isNameUnique]
  );

  const isPathTabLoading =
    isLoadingPaths || isLoadingAssignments || isLoadingAvailableUsers || isLoadingAssignmentDetails;

  // ─── useBlocker (replaces popstate / UnsavedAudienceChangesDialog) ────────

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isEditMode &&
      !isSubmitting &&
      unsavedAudienceChangesCount > 0 &&
      currentLocation.pathname !== nextLocation.pathname
  );

  // ─── Data loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isLoadingAvailableUsers) {
      setFormValues(prev => ({
        ...prev,
        target: { ...prev.target, availableUsers, allUsers },
      }));
    }
  }, [availableUsers, allUsers, isLoadingAvailableUsers]);

  const fetchPaths = useCallback(async () => {
    setIsLoadingPaths(true);
    try {
      const [data, levels] = await Promise.all([
        loadPathsAsync({ withDuration: true }),
        loadLevelsAsync(),
      ]);
      const all = [...(data.defaultPaths ?? []), ...(data.paths ?? []), ...(data.quests ?? [])]
        .filter(p => p.type !== "activities")
        .map(p => ({
          ...p,
          totalDuration: (() => {
            const fromMicroRoles = p.microRoles
              ?.flatMap(mr => mr.lessons ?? [])
              .reduce((sum, l) => sum + (l.durationNumber ?? 0), 0);
            if (fromMicroRoles != null) return fromMicroRoles;
            const flatLessons = p.versions?.[0]?.lessons ?? p.lessons ?? [];
            return flatLessons.reduce((sum, l) => sum + (l.durationNumber ?? 0), 0) || undefined;
          })(),
        }));
      setAvailablePaths(all);
      setLevelOptions(
        (levels as Level[])
          .filter((l: Level) => l.rankName !== "awareness" && l.name)
          .map((l: Level) => ({ label: l.name!, value: l.name! }))
      );
    } catch {
      showToast({ severity: "error", detail: "Failed to load paths" });
    } finally {
      setIsLoadingPaths(false);
    }
  }, [showToast]);

  const fetchAssignments = useCallback(async () => {
    setIsLoadingAssignments(true);
    try {
      const assignments = await loadAssignmentPoliciesAsync();
      setExistingAssignments(assignments);
    } catch {
      showToast({
        severity: "error",
        detail: "Failed to load existing assignments. Please refresh the page.",
      });
    } finally {
      setIsLoadingAssignments(false);
    }
  }, [showToast]);

  const hasFetchedAssignment = useRef(false);

  const fetchExistingAssignment = useCallback(async () => {
    if (!isEditMode || !id) return;
    setIsLoadingAssignmentDetails(true);
    try {
      const [assignmentPolicy, assignedUsers, assignmentRules] = await Promise.all([
        loadAssignmentPolicyByIdAsync(id),
        loadAssignedUsersAsync(id),
        loadAssignmentPolicyRulesAsync(id),
      ]);

      const currentAvailableUsers = formValues.target.availableUsers ?? [];
      const formData = await transformApiDataToFormValues(
        assignmentPolicy as Parameters<typeof transformApiDataToFormValues>[0],
        assignedUsers,
        availablePaths,
        currentAvailableUsers
      );

      setOriginalUsers([...(formData.target.users ?? [])]);
      setFormValues(prev => ({
        ...formData,
        target: {
          ...formData.target,
          availableUsers: prev.target.availableUsers,
          allUsers: prev.target.allUsers,
        },
      }));

      const newHireRule = assignmentRules.find(r => r.level === "No Level (New Learner)");
      if (newHireRule) {
        setNewHire(true);
        setNewHireRuleId(newHireRule.id ?? null);
      }

      const settings = formData.settings as FormSettings;
      const dateError =
        settings.activated === false ? false : !settings.endDate && !settings.dates?.dynamic;
      setValidationErrors({
        path: { hasError: !formData.selectedPath, message: "Please select a path" },
        audience: { hasError: false, message: "" },
        settings: {
          name: { hasError: false, message: "" },
          description: { hasError: false, message: "" },
          date: {
            hasError: dateError,
            message: dateError ? "Please select an end date" : "",
          },
        },
      });
    } catch {
      showToast({ severity: "error", detail: "Failed to load assignment. Please try again." });
    } finally {
      setIsLoadingAssignmentDetails(false);
    }
  }, [id, isEditMode, availablePaths, formValues.target.availableUsers, showToast]);

  useEffect(() => {
    fetchPaths();
    fetchAssignments();
  }, [fetchPaths, fetchAssignments]);

  useEffect(() => {
    if (isEditMode && availablePaths.length > 0 && !hasFetchedAssignment.current) {
      hasFetchedAssignment.current = true;
      fetchExistingAssignment();
    }
  }, [isEditMode, availablePaths, fetchExistingAssignment]);

  useEffect(() => {
    if (currentStep === steps.length - 1) {
      setShowValidationMessages(true);
    }
  }, [currentStep]);

  // ─── Form change / validation ─────────────────────────────────────────────

  const validateSection = (section: string, values: unknown) => {
    switch (section) {
      case "selectedPath":
        setValidationErrors(prev => ({
          ...prev,
          path: { hasError: !values, message: "Please select a path" },
        }));
        break;
      case "target":
        setValidationErrors(prev => ({
          ...prev,
          audience: { hasError: false, message: "" },
        }));
        break;
      case "settings": {
        const s = values as FormSettings & { description?: string };
        const nameError = !s.name || s.name.trim().length === 0;
        const descError = !s.description || s.description.trim().length === 0;
        const dateError =
          isEditMode && s.activated === false ? false : !s.endDate && !s.dates?.dynamic;
        const nameExists = Boolean(
          s.name &&
          existingAssignments.some(
            a => a.name?.trim() === s.name?.trim() && String(a.id) !== String(id)
          )
        );
        setValidationErrors(prev => ({
          ...prev,
          settings: {
            name: {
              hasError: nameError || nameExists,
              message: nameError
                ? "Assignment name is required"
                : nameExists
                  ? "An assignment with this name already exists"
                  : "",
            },
            description: { hasError: descError, message: "Assignment description is required" },
            date: { hasError: dateError, message: "Please select an end date" },
          },
        }));
        break;
      }
    }
  };

  const handleFormChange = (section: string, newValues: unknown) => {
    setFormValues(prev => ({ ...prev, [section]: newValues }));
    validateSection(section, newValues);
  };

  // ─── Step navigation ──────────────────────────────────────────────────────

  const handleStepChange = (newStep: number) => {
    if (currentStep === 0 && isPathTabLoading && newStep !== 0) return;
    if (checkUnsavedUsers(currentStep, newStep, 1)) return;
    setCurrentStep(newStep);
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const transformDataForPolicyCreation = (fv: FormValues): CreateAssignmentPayload => {
    const settings = fv.settings as FormSettings & { description?: string; name?: string };
    const selectedPath = fv.selectedPath;
    const isDueDate = !settings.dates?.dynamic;
    let dueDateAt: number | null = null;
    let duration = 0;

    if (isDueDate && settings.endDate) {
      dueDateAt = Math.floor(new Date(settings.endDate).getTime() / 1000);
    } else if (!isDueDate) {
      duration = settings.dates?.dueAfter?.days ?? 30;
    }

    return {
      name: settings.name ?? "",
      description: settings.description ?? "",
      activated: settings.activated !== false,
      dueDateAt,
      // Rails expects lowercase (e.g. "none", "weekly"). Normalise to guard
      // against any StartCase value that may have slipped through.
      notificationsFrequency: normalizeNotificationsFrequency(settings.sendReminders),
      roleId: selectedPath?.id,
      locale: "en",
      duration,
      enrollmentDelay: 0,
    };
  };

  const assignUsersToPolicyInternal = async (policyId: string, storedUsersArg: StoredUser[]) => {
    const userEmails = storedUsersArg
      .map(su => su.email)
      .filter((e): e is string => Boolean(e))
      .join(",");
    await getRailsClient().post(apiAssignmentPath(policyId), { userEmails });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      if (isEditMode && id) {
        setSubmissionProgress({ value: 20, label: "Updating assignment..." });
        const policyData = transformDataForPolicyCreation(formValues);
        await updateAssignmentPolicyAsync(id, policyData);

        setSubmissionProgress({ value: 40, label: "Processing user changes..." });
        const currentUsers = formValues.target.users ?? [];

        const removedUsers = originalUsers.filter(
          o => !currentUsers.some(c => c.directoryUuid === o.directoryUuid)
        );
        const addedUsers = currentUsers.filter(
          c => !originalUsers.some(o => o.directoryUuid === c.directoryUuid)
        );

        if (removedUsers.length > 0) {
          const idsToDelete = removedUsers
            .map(u => u.assignmentId)
            .filter((i): i is string => Boolean(i));
          if (idsToDelete.length > 0) await deleteAssignmentUsersAsync(id, idsToDelete);
        }

        if (addedUsers.length > 0) {
          await assignUsersToPolicyInternal(id, addedUsers);
        }

        if (addedUsers.length > 0 || removedUsers.length > 0) {
          setUsersWereModified(true);
          setProcessingAssignment(id);
        }

        setSubmissionProgress({ value: 80, label: "Updating hire rules..." });
        if (newHire && !newHireRuleId) {
          const ruleData: NewHireRulePayload = {
            user_level: "0",
            audience: null,
            company: null,
            country: null,
            department: null,
            managers_email: null,
            manager: null,
            security_champion: null,
            team: null,
            title: null,
            job_role: null,
          };
          await createAssignmentPolicyRuleAsync(id, ruleData);
        } else if (!newHire && newHireRuleId) {
          await deleteAssignmentPolicyRuleAsync(id, [newHireRuleId]);
        }

        setSubmissionProgress({ value: 100, label: "Complete!" });
      } else {
        setSubmissionProgress({ value: 25, label: "Creating assignment policy..." });
        const policyData = transformDataForPolicyCreation(formValues);
        const policyResponse = await createAssignmentAsync(policyData);
        const policyId = policyResponse.assignmentPolicy?.id;
        if (!policyId) throw new Error("No policy ID returned");

        setSubmissionProgress({ value: 75, label: "Assigning users..." });
        const selectedUsers = formValues.target.users ?? [];
        if (selectedUsers.length > 0) {
          await assignUsersToPolicyInternal(policyId, selectedUsers);
          setUsersWereModified(true);
          setProcessingAssignment(policyId);
        }

        if (newHire) {
          setSubmissionProgress({ value: 90, label: "Creating new hire rule..." });
          const ruleData: NewHireRulePayload = {
            user_level: "0",
            audience: null,
            company: null,
            country: null,
            department: null,
            managers_email: null,
            manager: null,
            security_champion: null,
            team: null,
            title: null,
            job_role: null,
          };
          await createAssignmentPolicyRuleAsync(policyId, ruleData);
        }

        setSubmissionProgress({ value: 100, label: "Complete!" });
      }

      setSubmissionSuccessful(true);
      navigate(AdminAppRoutes.routes.assignments.route);
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : isEditMode
            ? "Failed to update assignment"
            : "Failed to create assignment";
      setSubmissionError(msg);
      showToast({ severity: "error", detail: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      await deleteAssignmentPolicyAsync(id);
      showToast({ severity: "success", detail: "Assignment deleted successfully" });
      navigate(AdminAppRoutes.routes.assignments.route);
    } catch {
      showToast({ severity: "error", detail: "Failed to delete assignment" });
    } finally {
      setIsDeleting(false);
    }
  };

  // ─── Cancel guard ─────────────────────────────────────────────────────────

  const handleCancel = () => {
    confirmDialog({
      message: "Changes you made will not be saved.",
      header: "Are you sure?",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      icon: "pi pi-exclamation-circle",
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      headerStyle: { border: "none" },
      accept: () => navigate(AdminAppRoutes.routes.assignments.route),
    });
  };

  // ─── Steps ────────────────────────────────────────────────────────────────

  const steps = [
    {
      header: showValidationMessages
        ? `Path${!validationErrors.path.hasError ? " ✓" : " ✗"}`
        : "Path",
      title: "Path",
      content: (
        <PathSelection
          availablePaths={availablePaths}
          selectedPath={formValues.selectedPath}
          onPathSelect={path => handleFormChange("selectedPath", path)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.path}
          isLoading={isLoadingPaths || isLoadingAssignments}
          existingAssignments={existingAssignments}
          isEditMode={isEditMode}
          levelOptions={levelOptions}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Audience${!validationErrors.audience.hasError ? " ✓" : " ✗"}`
        : "Audience",
      title: "User Selection",
      content: (
        <AssignmentAudience
          values={formValues.target}
          handleChange={newValues => handleFormChange("target", newValues)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.audience}
          onSelectedUsersChange={handleSelectedUsersChange}
          onAddUsersAndContinue={addUsersAndContinueRef}
          selectedUsersToAdd={selectedUsersToAdd}
          setSelectedUsersToAdd={setSelectedUsersToAdd}
          selectedUsersToRemove={selectedUsersToRemove}
          setSelectedUsersToRemove={setSelectedUsersToRemove}
          selectedLevel={selectedLevel}
          onLevelChange={setSelectedLevel}
          showNewHire={true}
          newHireValue={newHire}
          onNewHireChange={v => setNewHire(v)}
          showProcessingMessage={showAudienceProcessingMessage}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Assignment Settings${
            !(
              validationErrors.settings.name.hasError ||
              validationErrors.settings.description.hasError ||
              validationErrors.settings.date.hasError
            )
              ? " ✓"
              : " ✗"
          }`
        : "Assignment Settings",
      title: "Assignment Settings",
      content: (
        <AssignmentSettings
          values={formValues.settings}
          handleChange={newValues => handleFormChange("settings", newValues)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.settings}
        />
      ),
    },
    {
      header: "Review",
      title: "Review Assignment",
      content: (
        <div className="space-y-4">
          <AssignmentReview
            values={formValues}
            onStepChange={setCurrentStep}
            validationErrors={validationErrors}
            isNameUnique={isNameUnique}
            isFormValid={isFormValid}
          />
        </div>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      {/* Page header */}
      <div className="flex items-center justify-between p-4">
        <h1>{isEditMode ? "Edit Assignment" : "Create New Assignment"}</h1>
        <Button
          label=""
          icon="pi pi-times"
          iconPos="right"
          onClick={handleCancel}
          severity="contrast"
          aria-label="Close"
        />
      </div>

      <div className={`p-4 min-h-[60vh] flex flex-col ${themeClasses.bgElevation1}`}>
        <div className="flex-1 flex flex-col">
          <FormStepper
            steps={steps}
            currentStep={currentStep}
            setCurrentStep={handleStepChange}
            handleSubmit={handleSubmit}
            isNew={true}
            isSubmitting={isSubmitting || isPathTabLoading}
            submitButtonText={isEditMode ? "Update Assignment" : "Create Assignment"}
            submitDisabled={!isFormValid || isSubmitting || isPathTabLoading || viewOnly}
          />
        </div>
        {isEditMode && currentStep === steps.length - 1 && (
          <div className="flex justify-end gap-3 mt-4">
            <DeleteAssignmentButton
              assignmentName={(formValues.settings as FormSettings & { name?: string }).name ?? ""}
              onDelete={handleDelete}
              isLoading={isDeleting}
              hasReadOnly={viewOnly}
            />
          </div>
        )}
      </div>

      {/* Dialogs */}
      <ConfirmDialog maskStyle={{ backdropFilter: "blur(5px)" }} />

      {/* Loading overlay */}
      <Dialog
        visible={isPathTabLoading}
        onHide={() => undefined}
        modal
        closable={false}
        showHeader={false}
        style={{ width: "300px", height: "300px" }}
        maskStyle={{ backdropFilter: "blur(5px)" }}
      >
        <div className="flex flex-col items-center justify-center gap-4 h-full">
          <p>Loading Assignment Data</p>
          <ProgressSpinner aria-label="Loading" style={{ width: "50px", height: "50px" }} />
        </div>
      </Dialog>

      {/* useBlocker leave guard */}
      <Dialog
        visible={blocker.state === "blocked"}
        onHide={() => blocker.reset?.()}
        header="Unsaved Changes"
        modal
        maskStyle={{ backdropFilter: "blur(5px)" }}
        className="w-full max-w-xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Stay" severity="secondary" onClick={() => blocker.reset?.()} />
            <Button label="Leave" severity="danger" onClick={() => blocker.proceed?.()} />
          </div>
        }
      >
        <div className="flex items-center gap-3">
          <i className={`pi pi-exclamation-triangle ${themeClasses.iconWarning} text-2xl`} />
          <div>
            <p className="mb-2">
              You have {unsavedAudienceChangesCount} unsaved user change
              {unsavedAudienceChangesCount !== 1 ? "s" : ""} in the Audience step.
            </p>
            <p className="body-sm">
              Are you sure you want to leave? Your changes will not be saved.
            </p>
          </div>
        </div>
      </Dialog>

      {/* Within-step user selection warning */}
      <UserSelectionConfirmDialog
        visible={showUserSelectionDialog}
        onHide={handleCancelDialog}
        onContinue={handleContinue}
        onAddUsersAndContinue={handleAddUsersAndContinue}
        selectedUsersCount={selectedUsersToAdd.length}
      />

      {/* Submit progress */}
      <SubmitProgressDialog
        isSubmitting={isSubmitting}
        submissionSuccessful={submissionSuccessful}
        submissionError={submissionError}
        submissionProgress={submissionProgress}
        onHide={() => {
          setSubmissionSuccessful(false);
          setSubmissionError(null);
        }}
        onNavigateTo={() => navigate(AdminAppRoutes.routes.assignments.route)}
        successButtonLabel="Go to Assignments"
        successMessage={
          isEditMode
            ? "Your assignment has been successfully updated!"
            : "Your assignment has been successfully created!"
        }
        successHeader={isEditMode ? "Assignment Updated" : "Assignment Created"}
        processHeader={isEditMode ? "Updating Assignment" : "Creating Assignment"}
        userUpdateInfoMessage={
          usersWereModified
            ? "We're updating the users in your assignment. You may navigate away from this page, and the process will continue."
            : ""
        }
      />
    </>
  );
};

export default NewAssignment;
